import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../../risikoanalyseStep/tokens";
import { KLASSEN_LABELS, VEHICLE_CLASS_TO_KEY } from "../../kfzContractStep/data";
import type { KlasseKey, RiskSummary } from "../../kfzContractStep/types";

/**
 * Lese-Ansicht aller im Vertrag hinterlegten KFZ-Risiken.
 * Kennzeichen + Hersteller/Modell + Klasse + VIN. Risiken werden vom Dispatcher
 * vorgeladen.
 */
const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  empty: {
    paddingTop: "12px",
    paddingBottom: "12px",
    color: BRAND.muted,
    fontStyle: "italic",
    fontSize: "0.92rem",
  },
  card: {
    position: "relative",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    display: "grid",
    gridTemplateColumns: "1fr",
    columnGap: "16px",
    rowGap: "6px",
    "@media (min-width: 720px)": {
      gridTemplateColumns: "minmax(180px, 0.6fr) 1fr 1fr 1fr",
      alignItems: "center",
    },
  },
  cardNew: {
    ...shorthands.borderColor("#2E7D4F"),
    backgroundColor: "rgba(46,125,79,0.05)",
    boxShadow: "0 0 0 1px rgba(46,125,79,0.18)",
  },
  newBadge: {
    position: "absolute",
    top: "-10px",
    right: "12px",
    backgroundColor: "#2E7D4F",
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.68rem",
    fontWeight: 800,
    letterSpacing: "0.06em",
    paddingTop: "3px",
    paddingBottom: "3px",
    paddingLeft: "8px",
    paddingRight: "8px",
    ...shorthands.borderRadius("999px"),
  },
  plate: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.05rem",
    color: BRAND.ink,
    fontWeight: 600,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
  },
  label: {
    fontSize: "0.72rem",
    fontWeight: 700,
    color: BRAND.muted,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  value: {
    fontSize: "0.9rem",
    color: BRAND.ink,
  },
  valueMuted: {
    color: BRAND.muted,
    fontStyle: "italic",
  },
});

const resolveKlasse = (chars: Record<string, string>): KlasseKey | null => {
  const v = chars.vehicleClass ?? "";
  if (!v) return null;
  for (const m of VEHICLE_CLASS_TO_KEY) {
    if (v.includes(m.contains)) return m.key;
  }
  return null;
};

const KfzRisikenDetails = ({
  risks,
  newRiskIds,
}: {
  risks: RiskSummary[];
  newRiskIds?: Set<number>;
}) => {
  const styles = useStyles();
  if (risks.length === 0) {
    return (
      <div className={styles.empty}>
        Keine Risiken hinterlegt.
      </div>
    );
  }
  return (
    <div className={styles.root}>
      {risks.map((r) => {
        const plate = r.characteristics.licensePlate || `Risiko ${r.id}`;
        const maker = r.characteristics.manufacturer;
        const model = r.characteristics.typeName;
        const vin = r.characteristics.vin;
        const klasse = resolveKlasse(r.characteristics);
        const isNew = newRiskIds?.has(r.id);
        return (
          <div key={r.id} className={mergeClasses(styles.card, isNew && styles.cardNew)}>
            {isNew && <span className={styles.newBadge}>NEU</span>}
            <span className={styles.plate}>{plate}</span>
            <div className={styles.field}>
              <span className={styles.label}>Hersteller / Modell</span>
              <span
                className={mergeClasses(styles.value, !(maker || model) && styles.valueMuted)}
              >
                {[maker, model].filter(Boolean).join(" ") || "—"}
              </span>
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Klasse</span>
              <span
                className={mergeClasses(styles.value, !klasse && styles.valueMuted)}
              >
                {klasse ? KLASSEN_LABELS[klasse] : "—"}
              </span>
            </div>
            <div className={styles.field}>
              <span className={styles.label}>VIN</span>
              <span
                className={mergeClasses(styles.value, !vin && styles.valueMuted)}
              >
                {vin || "—"}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default KfzRisikenDetails;
